import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext.jsx";
import { formatDisplayPrice } from "../utils/currency.js";
import { startHoverRest, cancelHoverRest } from "../utils/hoverRest.js";
import { useMediaQuery } from "../hooks/useMediaQuery.js";

// The product card: the catalog grid's card, shared by every grid that shows
// products — the catalog, the home page's NEW ARRIVALS row, and the "You may
// also like" rows on the product and cart pages. Those used to carry four
// copies of this markup that had drifted apart (the two related rows had no
// size, no sold overlay and a different cart button), and the copies'
// cart plus could not take out an item added with a size.

// Cards reveal in a stagger, but only the first few: past this index they all
// come in together. Uncapped, card 17 of the catalog waited two seconds and
// card 60 seven, so a fast scroll on a phone met a column of blank cells.
export const REVEAL_STAGGER_CAP = 8;

export function isItemSoldOut(item) {
  return Boolean(item.sold || item.state !== "LIVE" || !item.is_available);
}

// The inventory stores size and colour as one CSV ("34, Black"); the card
// shows the size alone.
function sizeLabelOf(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  return s.includes(",") ? s.split(",")[0].trim() : s;
}

export default function ProductCard({ item, to, state, index = 0, staggerMs = 120, className = "" }) {
  const { items: cartItems, addItem, removeItem } = useCart();
  // The second photo only ever shows under a mouse (the swap rules are
  // (hover: hover) only), so a touchscreen does not download it: on a phone
  // it was half the image bytes of every grid.
  const canHover = useMediaQuery("(hover: hover)");

  const photos = Array.isArray(item.photos_json) ? item.photos_json : [];
  const coverIndex = item.cover_index || 0;
  const imageUrl = photos[coverIndex]?.url || photos[0]?.url || null;
  const hoverUrl = photos.length > 1
    ? (photos[(coverIndex + 1) % photos.length]?.url || null)
    : null;
  const priceFormatted = item.price_usd != null
    ? formatDisplayPrice(item.price_usd, "USD")
    : "Price unavailable";
  const originalPriceFormatted = item.original_price_usd
    ? formatDisplayPrice(item.original_price_usd, "USD")
    : null;
  const isSoldOut = isItemSoldOut(item);
  const sizeLabel = sizeLabelOf(item.size);

  // Every cart entry for this piece, whatever size / colour it was added
  // with. An entry's own id is the inventory id; the hydrated item is a
  // fallback for older entries.
  const cartEntries = cartItems.filter((c) => c.id === item.id || c.item?.id === item.id);
  const inCart = cartEntries.length > 0;

  const handleToggleCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (inCart) {
      // The reducer removes by an exact (id, size, colour) match, so each
      // entry goes with its own variant. Removing by id alone (size null)
      // left anything added from the product page, with its size, stuck.
      for (const entry of cartEntries) {
        removeItem(entry.id, entry.size ?? null, entry.color ?? null);
      }
    } else {
      addItem(item);
    }
  };

  const delay = Math.min(index, REVEAL_STAGGER_CAP) * staggerMs;

  return (
    <Link
      to={to}
      state={state}
      className={`product-card product-card--reveal${isSoldOut ? " product-card--sold" : ""}${className ? ` ${className}` : ""}`}
      style={{ animationDelay: `${delay}ms` }}
      // The hover-rest timer is a mouse behaviour; a tap is not a hover.
      onPointerEnter={(e) => { if (e.pointerType === "mouse") startHoverRest(e.currentTarget); }}
      onPointerLeave={(e) => { if (e.pointerType === "mouse") cancelHoverRest(e.currentTarget); }}
    >
      <div className="product-image">
        {imageUrl ? (
          <>
            <img src={imageUrl} alt={item.title || ""} loading="lazy" className="product-image__main" />
            {hoverUrl && canHover && (
              <img src={hoverUrl} alt="" loading="lazy" className="product-image__hover" />
            )}
          </>
        ) : (
          <div className="product-image-placeholder">No image</div>
        )}
        {/* SOLD OUT across the photo (the owner, 2026-10-17: "for sold items
            put sold out banner over the listing"). It replaced the box under
            the price on these cards, so a piece says it once. */}
        {isSoldOut && <div className="product-card__sold-banner">Sold out</div>}
      </div>
      <div className="product-info-row">
        <div className="product-info-text">
          <div className="product-name">{item.title || "Untitled"}</div>
          {sizeLabel && <div className="product-size">{sizeLabel}</div>}
          {item.price_adjusted && originalPriceFormatted ? (
            <div className="product-price-container">
              <span className="product-price-original">{originalPriceFormatted}</span>
              <span className="product-price">{priceFormatted}</span>
            </div>
          ) : (
            <div className="product-price">{priceFormatted}</div>
          )}
        </div>
        {!isSoldOut && !item.showcase && (
          <button
            type="button"
            className={`product-cart-toggle${inCart ? " product-cart-toggle--active" : ""}`}
            aria-label={inCart ? "Remove from cart" : "Add to cart"}
            aria-pressed={inCart}
            onClick={handleToggleCart}
            onPointerEnter={(e) => { if (e.pointerType === "mouse") e.currentTarget.closest(".product-card")?.classList.add("product-card--no-hover"); }}
            onPointerLeave={(e) => { if (e.pointerType === "mouse") e.currentTarget.closest(".product-card")?.classList.remove("product-card--no-hover"); }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        )}
      </div>
    </Link>
  );
}
